/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Check, NotebookPen, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  FORMAT_OPTIONS,
  SHORTCUT_OPTIONS,
  STARSCRIBE_NUMBER_FIELDS,
  SURFACE_OPTIONS,
  starscribeBridgeLines,
  starscribeForm,
  starscribePatch,
  starscribeReadFailure,
  starscribeStatusText,
  starscribeSwitchText,
  validateStarscribe,
  type StarscribeAdminStatus,
  type StarscribeAdminView,
  type StarscribeForm,
} from '../lib/starscribe';
import { Button, Checkbox, ConfirmDialog, Field, Icon, NumberInput, Panel, Skeleton } from '../ui';
import { PeoplePicker } from './PeoplePicker';
import { SaveNote } from './SaveNote';

type Read = { status: 'loading' } | { status: 'failed'; text: string } | { status: 'loaded'; view: StarscribeAdminView; counts: StarscribeAdminStatus | null };

/**
 * Governance → StarScribe (docs/STRATEGY-starscribe.md section 11): where it shows, sharing, the export formats, the model
 * Gyro's shortcuts run on, and the limits with plain names (GET/PUT /v1/admin/starscribe); counts only from
 * GET .../status (IT never reads pages here); and a leaver's pages handed to a colleague (POST .../handoff, confirmed).
 * The switch itself is the `starscribe` flag in the Feature flags table, as PPP's is.
 */
export function StarscribeCard() {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [form, setForm] = useState<StarscribeForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [handing, setHanding] = useState(false);
  const [handoffNote, setHandoffNote] = useState<{ ok: boolean; text: string } | null>(null);
  const loadedOnce = useRef(false);

  // The counts are a nicety: a failed status read leaves the settings usable.
  const readBoth = async () => {
    const [view, counts] = await Promise.all([api.starscribe(), api.starscribeStatus().catch(() => null)]);
    return { view, counts };
  };

  const load = async (fresh: boolean) => {
    try {
      const { view, counts } = await readBoth();
      loadedOnce.current = true;
      setRead({ status: 'loaded', view, counts });
      if (fresh || !form) setForm(starscribeForm(view.settings));
      setNote((current) => (current && !current.ok ? null : current));
    } catch (error) {
      if (loadedOnce.current) setNote({ ok: false, text: starscribeReadFailure(error) });
      else setRead({ status: 'failed', text: starscribeReadFailure(error) });
    }
  };

  useEffect(() => {
    let live = true;
    readBoth().then(
      ({ view, counts }) => {
        if (!live) return;
        loadedOnce.current = true;
        setRead({ status: 'loaded', view, counts });
        setForm(starscribeForm(view.settings));
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: starscribeReadFailure(error) });
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load(false);
    } finally {
      setRefreshing(false);
    }
  };

  const save = async () => {
    if (read.status !== 'loaded' || !form) return;
    setNote(null);
    const problem = validateStarscribe(form);
    if (problem) {
      setNote({ ok: false, text: problem });
      return;
    }
    const patch = starscribePatch(form, read.view.settings);
    if (Object.keys(patch).length === 0) {
      setNote({ ok: true, text: 'Nothing changed.' });
      return;
    }
    setSaving(true);
    try {
      const view = await api.saveStarscribe(patch);
      setRead({ status: 'loaded', view, counts: read.counts });
      setForm(starscribeForm(view.settings));
      setNote({ ok: true, text: 'StarScribe settings saved. They apply within 30 seconds, and the change is in the audit trail.' });
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'The StarScribe settings were not saved.') });
    } finally {
      setSaving(false);
    }
  };

  const handOff = async () => {
    setHanding(true);
    try {
      const { moved } = await api.starscribeHandoff(from.trim(), to.trim());
      setConfirming(false);
      setHandoffNote({ ok: true, text: moved === 1 ? '1 page moved.' : `${moved} pages moved.` });
      setFrom('');
      setTo('');
      void load(false);
    } catch (error) {
      setConfirming(false);
      setHandoffNote({ ok: false, text: errorText(error, 'The pages were not moved.') });
    } finally {
      setHanding(false);
    }
  };

  const set = <K extends keyof StarscribeForm>(key: K, value: StarscribeForm[K]) => setForm((current) => (current ? { ...current, [key]: value } : current));
  const toggle = <V extends string>(list: V[], value: V, on: boolean): V[] => (on ? [...new Set([...list, value])] : list.filter((item) => item !== value));

  return (
    <Panel
      title={<span className="ui-title-icon"><Icon icon={NotebookPen} size="md" />StarScribe</span>}
      description="Pages beside the chat that people and Gyro edit together: where they show, sharing, exports and the limits"
      actions={read.status === 'loaded' ? <Button size="sm" icon={RefreshCw} busy={refreshing} onClick={() => void refresh()}>Refresh status</Button> : null}
      footer={<span>StarScribe itself is switched on with the <code>starscribe</code> flag under Feature flags. IT never reads pages here.</span>}
    >
      {read.status === 'failed' ? (
        <div className="ui-form">
          <p className="ui-note is-bad" role="alert"><span>{read.text}</span></p>
          <div className="ui-actions"><Button icon={RefreshCw} onClick={() => { setRead({ status: 'loading' }); void load(true); }}>Retry</Button></div>
        </div>
      ) : read.status === 'loading' || !form ? (
        <div className="ui-form" aria-busy="true">
          <span className="ui-sr">Loading the StarScribe settings</span>
          <Skeleton size="md" width="280px" /><Skeleton size="lg" /><Skeleton size="lg" />
        </div>
      ) : (
        <div className="ui-form">
          <div className="ui-callout ui-callout--neutral" role="status" aria-label="StarScribe status">
            <div className="ui-callout-text">
              <p className="ui-callout-title">{starscribeSwitchText(read.view)}</p>
              <div className="ui-callout-body">
                {read.counts ? <p className="ui-para">{starscribeStatusText(read.counts)}</p> : <p className="ui-para">The counts could not be read just now.</p>}
                {starscribeBridgeLines(read.view).map((line) => <p key={line} className="ui-para">{line}</p>)}
              </div>
            </div>
          </div>

          <fieldset className="ui-fieldset ui-field">
            <legend className="ui-field-label">Where StarScribe shows</legend>
            <div className="ui-actions">
              {SURFACE_OPTIONS.map((option) => (
                <Checkbox key={option.value} label={option.label} checked={form.surfaces.includes(option.value)} onChange={(e) => set('surfaces', toggle(form.surfaces, option.value, e.target.checked))} />
              ))}
            </div>
            <p className="ui-field-note">A place left out hides StarScribe there; nothing is deleted.</p>
          </fieldset>

          <div className="ui-field">
            <Checkbox label="Sharing with colleagues" checked={form.sharing} onChange={(e) => set('sharing', e.target.checked)} />
            <p className="ui-field-note">Off hides shared pages from the people they were shared with and keeps every share, so on again restores them.</p>
          </div>

          <fieldset className="ui-fieldset ui-field">
            <legend className="ui-field-label">Export formats</legend>
            <div className="ui-actions">
              {FORMAT_OPTIONS.map((option) => (
                <Checkbox key={option.value} label={option.label} checked={form.exportFormats.includes(option.value)} onChange={(e) => set('exportFormats', toggle(form.exportFormats, option.value, e.target.checked))} />
              ))}
            </div>
            <p className="ui-field-note">Word and PDF carry the Exante360 logo header. None ticked removes Export from pages.</p>
          </fieldset>

          <fieldset className="ui-fieldset ui-field">
            <legend className="ui-field-label">Gyro's shortcuts run on</legend>
            <div className="ui-actions">
              {SHORTCUT_OPTIONS.map((option) => (
                <label key={option.value} className="ui-check">
                  <input type="radio" name="starscribe-shortcut-model" value={option.value} checked={form.shortcutModel === option.value} onChange={() => set('shortcutModel', option.value)} />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <p className="ui-field-note">The page's Gyro shortcuts (Make it shorter, Professional, Add a conclusion…). Questions in the chat always use the chat's model.</p>
          </fieldset>

          <div className="ui-grid ui-grid--2">
            {STARSCRIBE_NUMBER_FIELDS.map((spec) => (
              <Field key={spec.key} label={spec.label} help={`${spec.hint} ${spec.bounds.min.toLocaleString('en-US')}–${spec.bounds.max.toLocaleString('en-US')}.`}>
                <NumberInput id={`starscribe-${spec.key}`} aria-label={spec.label} inputMode="decimal" className="ui-w-sm" value={form[spec.key]} onChange={(e) => set(spec.key, e.target.value)} />
              </Field>
            ))}
          </div>

          <div className="ui-actions">
            <Button variant="primary" icon={Check} busy={saving} onClick={() => void save()}>Save StarScribe settings</Button>
          </div>
          <SaveNote note={note} />

          <fieldset className="ui-fieldset ui-field">
            <legend className="ui-field-label">Hand a leaver's pages to a colleague</legend>
            <div className="ui-field">
              <label className="ui-field-label" htmlFor="starscribe-handoff-from">Person leaving</label>
              <PeoplePicker id="starscribe-handoff-from" label="Person leaving" value={from} max={1} onChange={setFrom} />
            </div>
            <div className="ui-field">
              <label className="ui-field-label" htmlFor="starscribe-handoff-to">Colleague who takes their pages</label>
              <PeoplePicker id="starscribe-handoff-to" label="Colleague who takes their pages" value={to} max={1} onChange={setTo} />
            </div>
            <p className="ui-field-note">Every page they own moves, with its history and shares; their open pages close. Recorded in the audit trail.</p>
            <div className="ui-actions">
              <Button icon={ArrowRightLeft} disabled={!from.trim() || !to.trim()} onClick={() => { setHandoffNote(null); setConfirming(true); }}>Move their pages</Button>
            </div>
            <SaveNote note={handoffNote} />
          </fieldset>

          <ConfirmDialog
            open={confirming}
            title="Move their pages?"
            confirmLabel="Move pages"
            busy={handing}
            onConfirm={() => void handOff()}
            onCancel={() => setConfirming(false)}
          >
            {`Every StarScribe page ${from.trim() || 'this person'} owns becomes ${to.trim() || 'the colleague'}'s. This cannot be undone here.`}
          </ConfirmDialog>
        </div>
      )}
    </Panel>
  );
}
